/**
 * Product Name Helper Utilities
 *
 * Centralized logic for handling product name display with fallback patterns.
 */

/**
 * Minimal product shape required for display name logic
 */
export type ProductNameFields = {
  preferred_product_name?: string | null;
};

/**
 * Get the display name for a product.
 *
 * `preferred_product_name` reads the server-side materialized `display_name`
 * column (AD-16/AD-28), which implements the full name-precedence fallback
 * chain (user-set name, Orange Book ingredient, CAS name, etc.). That column
 * is still genuinely nullable, though: sp_recompute_display_name explicitly
 * nulls it for a product with zero eligible name candidates anywhere (see
 * sql/migrations/V1_3_23__create_sp_recompute_display_name.sql), so a blank
 * client-side fallback here would render an empty Product Name cell and an
 * empty grouping key. Falls back to the same '—' placeholder already used
 * elsewhere in ProductsRowGroupingDataGrid for other missing values.
 *
 * @param product - Product object or partial with name fields
 * @returns The product's display name
 *
 * @example
 * ```typescript
 * const product = { preferred_product_name: 'Aspirin' };
 * getProductDisplayName(product); // Returns: 'Aspirin'
 * ```
 */
export function getProductDisplayName(product: ProductNameFields): string {
  return product.preferred_product_name ?? '—';
}
